import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { AuthCardIntro, AuthCardTopBar } from "../components/AuthCardHeader";
import { PasswordStrengthIndicator } from "../components/PasswordStrengthIndicator";
import {
  getPasswordRequirements,
  isPasswordValid,
} from "../utils/passwordValidation";
import { ROUTES } from "../../../shared/utils";
import {
  useCompletePasswordReset,
  useForgotPassword,
  useVerifyPasswordReset,
} from "../mutations";

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const verifyResetMutation = useVerifyPasswordReset();
  const completeResetMutation = useCompletePasswordReset();
  const requestResetMutation = useForgotPassword();

  const [verificationError, setVerificationError] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [completeError, setCompleteError] = useState<string>("");
  const [completeSuccess, setCompleteSuccess] = useState<string>("");

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [requestError, setRequestError] = useState<string>("");
  const [requestSuccess, setRequestSuccess] = useState<string>("");

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);
  const isEmailValid = emailRegex.test(email);
  const showEmailError = emailTouched && !isEmailValid;

  const passwordRequirements = useMemo(() => {
    return getPasswordRequirements(password, confirmPassword);
  }, [password, confirmPassword]);

  const isPasswordValidCheck = isPasswordValid(passwordRequirements);
  const showPasswordError = touched && !isPasswordValidCheck;
  const showConfirmPasswordError =
    touched &&
    !passwordRequirements.find((req) => req.key === "passwordsMatch")?.isValid;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setVerificationError("");
      setExpiresAt(null);

      if (!token) return;

      try {
        const result = await verifyResetMutation.mutateAsync({ token });
        if (cancelled) return;

        if (!result?.valid) {
          setVerificationError(result?.error || "Reset token invalid or expired.");
          return;
        }

        setExpiresAt(result.expiresAt ?? null);
      } catch {
        if (cancelled) return;
        setVerificationError("Reset token invalid or expired.");
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleRequestReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailTouched(true);
    setRequestError("");
    setRequestSuccess("");

    if (!isEmailValid) return;

    try {
      await requestResetMutation.mutateAsync({ username: email.trim() });
      setRequestSuccess("Reset link sent. Please check your email.");
    } catch (error: unknown) {
      setRequestError(
        error instanceof Error
          ? error.message
          : "Failed to send password reset link"
      );
    }
  };

  const handleCompleteReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setCompleteError("");
    setCompleteSuccess("");

    if (!token || !isPasswordValidCheck) return;

    try {
      const result = await completeResetMutation.mutateAsync({
        token,
        newPassword: password,
      });
      setCompleteSuccess(result?.message || "Password reset successfully.");
      setTimeout(() => navigate(ROUTES.LOGIN), 800);
    } catch (error: unknown) {
      let errorMessage = "Reset token invalid or expired.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; message?: string } };
        };
        errorMessage =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setCompleteError(errorMessage);
    }
  };

  const isVerifying = !!token && verifyResetMutation.isPending;
  const isTokenValid = !!token && !verificationError && !isVerifying;

  if (isVerifying) {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <AuthCardTopBar />
            <CircularProgress />
            <Typography color="text.secondary">
              Verifying your reset link…
            </Typography>
          </Stack>
        </Box>
      </Card>
    );
  }

  if (requestSuccess) {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Stack spacing={4}>
            <AuthCardTopBar />
            <AuthCardIntro
              title="Check your email"
              subtitle="If an account exists for that email, we've sent a password reset link."
            />

            <Alert severity="success">{requestSuccess}</Alert>

            <Button
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{ mt: 2 }}
            >
              Return to Sign In
            </Button>
          </Stack>
        </Box>
      </Card>
    );
  }

  if (isTokenValid) {
    return (
      <Card
        component="form"
        onSubmit={handleCompleteReset}
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Stack spacing={4}>
            <AuthCardTopBar />
            <AuthCardIntro
              title="Reset your password"
              subtitle="Create a new password to regain access to your account."
            />

            {completeError && <Alert severity="error">{completeError}</Alert>}
            {completeSuccess && (
              <Alert severity="success">{completeSuccess}</Alert>
            )}

            <Stack spacing={2}>
              <TextField
                fullWidth
                label="New password"
                value={password}
                type={showPassword ? "text" : "password"}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched(true)}
                error={showPasswordError}
                placeholder="Enter your new password"
                helperText={
                  showPasswordError
                    ? "Password must meet all requirements below."
                    : ""
                }
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlineRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm password"
                value={confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onBlur={() => setTouched(true)}
                error={showConfirmPasswordError}
                placeholder="Confirm your new password"
                helperText={
                  showConfirmPasswordError ? "Passwords do not match." : ""
                }
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlineRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {password && (
              <PasswordStrengthIndicator
                requirements={passwordRequirements}
                showProgress={true}
              />
            )}

            {expiresAt && (
              <Typography variant="caption" color="text.secondary">
                Link expires: {new Date(expiresAt).toLocaleString()}
              </Typography>
            )}

            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={
                !password.length ||
                !confirmPassword.length ||
                !isPasswordValidCheck ||
                completeResetMutation.isPending
              }
              sx={{ mt: 2 }}
            >
              {completeResetMutation.isPending
                ? "Resetting…"
                : "Set new password"}
            </Button>

            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              variant="body2"
              underline="hover"
              sx={{ alignSelf: "center", fontWeight: 500 }}
            >
              Back to sign in
            </Link>
          </Stack>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      component="form"
      onSubmit={handleRequestReset}
      variant="outlined"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "divider",
        borderRadius: 4,
      }}
    >
      <Box sx={{ p: 4 }}>
        <Stack spacing={4}>
          <AuthCardTopBar />
          <AuthCardIntro
            title={token ? "Reset link expired" : "Forgot your password?"}
            subtitle={
              token
                ? "This reset link is invalid or has expired. Request a new one below."
                : "Enter your email and we'll send you a password reset link."
            }
          />

          {verificationError && (
            <Alert severity="error">{verificationError}</Alert>
          )}
          {requestError && <Alert severity="error">{requestError}</Alert>}

          <TextField
            fullWidth
            label="Email address"
            value={email}
            type="email"
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            error={showEmailError}
            placeholder="Enter your email address"
            helperText={showEmailError ? "Please enter a valid email address." : ""}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            disabled={!email.length || requestResetMutation.isPending}
            sx={{ mt: 2 }}
          >
            {requestResetMutation.isPending ? "Sending…" : "Send reset link"}
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", fontWeight: 400 }}
          >
            Remembered your password?{" "}
            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Sign in
            </Link>
          </Typography>
        </Stack>
      </Box>
    </Card>
  );
};
